/**
 * The inline-chip family — shared style constants for the flat, in-text chip
 * that `EntityBadge`'s `inline` variant renders, exported for the cases where
 * the chip has to be a different element (an interactive trigger chip with an
 * arbitrary icon, e.g. the proposal header's trigger context chips) and
 * `EntityBadge` itself cannot be used. Import these instead of copying the
 * class strings so every inline chip stays pixel-identical; this mirrors the
 * export-for-custom-elements contract `../../../base/badges/chip-styles.ts`
 * documents for the pill/badge family (a DIFFERENT family — do not mix them).
 *
 * Geometry rationale (lifted verbatim with the constants from EntityBadge,
 * which mirrors the legacy Plate `EntityElement` so migrated markdown
 * surfaces look identical):
 *   - flat `rounded` (4px, not pill)
 *   - no vertical padding (preserves line-height in flowing text)
 *   - text-sm with align-middle for baseline alignment
 *   - always reserves a 1px transparent border slot so chips on the same
 *     line share the same outer-box height
 */

export const INLINE_CHIP_BASE =
  'inline-flex items-center px-1.5 rounded font-medium text-sm align-middle gap-0.5 border border-transparent my-px';

/** Resting data chip (EntityBadge `inline` default). */
export const INLINE_CHIP_DATA = 'bg-utility-gray-100 text-utility-gray-700';

/** Added counterpart of `INLINE_CHIP_DATA` — same shape, success tint. */
export const INLINE_CHIP_ADDED =
  'bg-utility-success-50 text-utility-success-700 ring-1 ring-utility-success-200';

export const INLINE_CHIP_FAILED = 'bg-utility-error-50 text-utility-error-700';

/** 14px leading glyph — matches the legacy Plate EntityElement chip. */
export const INLINE_CHIP_ICON = 'size-3.5 shrink-0';

/**
 * Ghost chip: same geometry, no resting fill. For chips sitting in a dense
 * toolbar/header row rather than flowing text.
 */
export const INLINE_CHIP_IDLE =
  'bg-transparent text-utility-gray-700 hover:bg-utility-gray-100';

/**
 * Selected chip — the sanctioned soft brand tint
 * (VISUAL_STYLE_GUIDE.md § Brand: soft/tinted selected chips).
 */
export const INLINE_CHIP_SELECTED =
  'bg-utility-brand-50 text-utility-brand-700 ring-1 ring-utility-brand-200 hover:bg-utility-brand-50';

/**
 * Activation affordance + the canonical focus recipe
 * (VISUAL_STYLE_GUIDE.md § Focus rings).
 */
export const INLINE_CHIP_INTERACTIVE =
  'cursor-pointer transition duration-100 ease-linear outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2';
